import { useState } from 'react';
import { ChevronDown, Plus, Building2, Layers } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { agenciesApi, workspacesApi } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';

export function AgencyWorkspaceSwitcher() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const {
    currentAgency,
    currentWorkspace,
    setCurrentAgency,
    setCurrentWorkspace,
  } = useAppStore();

  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState<'agency' | 'workspace' | null>(null);
  const [newName, setNewName] = useState('');

  const { data: agenciesData } = useQuery({
    queryKey: ['agencies'],
    queryFn: async () => {
      const token = await getToken();
      return agenciesApi.list(token!);
    },
  });

  const { data: workspacesData } = useQuery({
    queryKey: ['workspaces', currentAgency?.id],
    queryFn: async () => {
      const token = await getToken();
      return workspacesApi.list(token!, currentAgency!.id);
    },
    enabled: !!currentAgency,
  });

  // Auto-selection of the active agency/workspace lives in useScopeAutoSelect
  // (mounted in Shell), so it also runs while the sidebar is collapsed.

  const createAgency = useMutation({
    mutationFn: async (name: string) => {
      const token = await getToken();
      return agenciesApi.create(token!, name);
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['agencies'] });
      setCurrentAgency(data.agency);
      setShowCreate(null);
      setNewName('');
    },
  });

  const createWorkspace = useMutation({
    mutationFn: async (name: string) => {
      const token = await getToken();
      return workspacesApi.create(token!, {
        agency_id: currentAgency!.id,
        name,
      });
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['workspaces', currentAgency?.id] });
      setCurrentWorkspace(data.workspace);
      setShowCreate(null);
      setNewName('');
    },
  });

  const agencies = agenciesData?.agencies ?? [];
  const workspaces = workspacesData?.workspaces ?? [];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Switch workspace"
        className="group flex w-full items-center justify-between gap-2 rounded-l bg-card px-2.5 py-2 text-left ring-1 ring-inset ring-hairline transition-[background-color,box-shadow] duration-fast ease-sfx hover:ring-hairline-strong"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="sfx-icon-tile h-8 w-8">
            <Layers className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            {currentWorkspace ? (
              <>
                <p className="text-[13px] font-semibold leading-tight text-foreground truncate">{currentWorkspace.name}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground truncate">{currentAgency?.name}</p>
              </>
            ) : currentAgency ? (
              <p className="text-xs font-medium text-foreground truncate">{currentAgency.name}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Select workspace</p>
            )}
          </div>
        </div>
        <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform duration-base ease-sfx', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="sfx-pop sfx-pop-down absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-l bg-popover shadow-kumo-xl ring-1 ring-hairline">
          <div className="p-2 max-h-80 overflow-y-auto">
            {agencies.map((agency) => {
              const isCurrentAgency = currentAgency?.id === agency.id;
              return (
                <div key={agency.id}>
                  <button
                    onClick={() => {
                      setCurrentAgency(agency);
                      setCurrentWorkspace(null);
                    }}
                    className={cn(
                      'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors',
                      isCurrentAgency
                        ? 'bg-accent/15 text-foreground'
                        : 'text-foreground hover:bg-tertiary'
                    )}
                  >
                    <Building2 className={cn('h-3.5 w-3.5 shrink-0', isCurrentAgency ? 'text-accent' : 'text-muted-foreground')} />
                    <span className="text-xs font-medium">{agency.name}</span>
                  </button>

                  {isCurrentAgency &&
                    workspaces.map((ws) => {
                      const isCurrentWs = currentWorkspace?.id === ws.id;
                      return (
                        <button
                          key={ws.id}
                          onClick={() => {
                            setCurrentWorkspace(ws);
                            setOpen(false);
                          }}
                          className={cn(
                            'w-full flex items-center gap-2 px-2 py-1.5 pl-6 rounded-md text-left transition-colors',
                            isCurrentWs
                              ? 'bg-accent/25 text-foreground font-medium'
                              : 'text-foreground/80 hover:bg-tertiary hover:text-foreground'
                          )}
                        >
                          <Layers className={cn('h-3 w-3 shrink-0', isCurrentWs ? 'text-accent' : 'text-muted-foreground')} />
                          <span className="text-xs truncate">{ws.name}</span>
                        </button>
                      );
                    })}

                  {isCurrentAgency && (
                    <button
                      onClick={() => { setShowCreate('workspace'); setOpen(false); }}
                      className="w-full flex items-center gap-2 px-2 py-1.5 pl-6 rounded-md text-left hover:bg-tertiary hover:text-foreground transition-colors text-muted-foreground"
                    >
                      <Plus className="h-3 w-3 shrink-0" />
                      <span className="text-xs">New workspace</span>
                    </button>
                  )}
                </div>
              );
            })}

            <div className="border-t border-border mt-1 pt-1">
              <button
                onClick={() => { setShowCreate('agency'); setOpen(false); }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-tertiary hover:text-foreground transition-colors text-muted-foreground"
              >
                <Plus className="h-3.5 w-3.5 shrink-0" />
                <span className="text-xs">New agency</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create dialog */}
      {showCreate && (
        <div className="sfx-fade fixed inset-0 z-[100] flex items-center justify-center bg-[hsl(var(--sfx-brand-deep)/0.6)] backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-entity-title"
            className="sfx-pop w-[22rem] rounded-xl bg-popover p-5 shadow-kumo-xl ring-1 ring-hairline"
          >
            <h3 id="create-entity-title" className="font-display text-base font-semibold text-foreground">
              {showCreate === 'agency' ? 'Create agency' : 'Create workspace'}
            </h3>
            <p className="mt-1 mb-4 text-xs text-muted-foreground">
              {showCreate === 'agency'
                ? 'An agency groups the client workspaces your team manages.'
                : 'One workspace per client brand: its accounts, Brand Kit and calendar.'}
            </p>
            <label htmlFor="create-entity-name" className="mb-1.5 block text-xs font-medium text-foreground">
              Name
            </label>
            <input
              id="create-entity-name"
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newName.trim()) {
                  if (showCreate === 'agency') createAgency.mutate(newName.trim());
                  else createWorkspace.mutate(newName.trim());
                }
                if (e.key === 'Escape') setShowCreate(null);
              }}
              placeholder={showCreate === 'agency' ? 'Agency name' : 'Workspace name'}
              className="h-10 w-full rounded-m bg-input px-3 text-sm text-foreground outline-none ring-1 ring-inset ring-border transition-shadow duration-fast focus:ring-2 focus:ring-accent"
            />
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" size="md" onClick={() => setShowCreate(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                loading={createAgency.isPending || createWorkspace.isPending}
                onClick={() => {
                  if (!newName.trim()) return;
                  if (showCreate === 'agency') createAgency.mutate(newName.trim());
                  else createWorkspace.mutate(newName.trim());
                }}
                disabled={!newName.trim()}
              >
                {showCreate === 'agency' ? 'Create agency' : 'Create workspace'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
